import {
    AdditiveBlending,
    Color,
    InstancedMesh,
    Matrix4,
    MeshBasicMaterial,
    SphereGeometry,
    Vector3,
} from 'three';

import { disposeObject } from '@resn/gozer-three/helpers';

import { shaderChunks } from '../../shaders/ShaderChunks/transitions/burn';

class BurnTransition {
    static uniforms = shaderChunks.uniforms;
    static vertex = shaderChunks.vertex;
    static fragment = shaderChunks.fragment;

    constructor(object) {
        this.object = object;
        this.name = 'burn';
        this.bloomSelection = [];
        this.transparent = true;

        // Particle system properties
        this.particleCount = 75;
        this.particleSystem = null;
        this.particleData = [];
        this.gravity = 0.00018;
        this.packForce = new Vector3();
        this.packForceRotation = new Vector3();

        // Edge detection parameters
        this.edgeThreshold = 0.005;
        this.mesh = null;
    }

    setTransitionUniforms(shader) {
        shader.transparent = this.transparent;
        shader.USE_TRANSITION_BURN = true;
        // shader.needsUpdate = true;
    }

    createCustomMeshes(parent) {
        // Create instanced mesh for particles
        const sphereGeometry = new SphereGeometry(0.003, 8, 8);
        const sphereMaterial = new MeshBasicMaterial({
            color: 0x00aaff,
            transparent: true,
            blending: AdditiveBlending,
            depthWrite: false,
        });
        sphereMaterial.name = 'BurnTransitionParticle';

        this.particleSystem = new InstancedMesh(sphereGeometry, sphereMaterial, this.particleCount);
        this.particleSystem.name = 'BurnTransitionParticles';
        this.particleSystem.instanceMatrix.setUsage(35044);
        this.particleSystem.count = 0;
        this.particleSystem.frustumCulled = false;

        // Initialize all matrices to identity and populate particle data
        const identityMatrix = new Matrix4();
        for (let i = 0; i < this.particleCount; i++) {
            this.particleSystem.setMatrixAt(i, identityMatrix);
            this.particleData.push({
                active: false,
                position: new Vector3(0, 0, 0),
                velocity: new Vector3(
                    (Math.random() - 0.5) * 0.004,
                    Math.random() * 0.015,
                    (Math.random() - 0.5) * 0.004
                ),
                scale: Math.random() * 0.3 + 0.2,
                lifeTime: 0,
                maxLifeTime: Math.random() * 1.5 + 1,
                rotation: new Vector3(
                    Math.random() * Math.PI,
                    Math.random() * Math.PI,
                    Math.random() * Math.PI
                ),
                color: new Color().setHSL(Math.random() * 0.2 + 0.4, 0.9, 0.7),
            });
        }

        this.particleSystem.instanceMatrix.needsUpdate = true;
        parent.add(this.particleSystem);

        this.bloomSelection.push(this.particleSystem);

        this.parent = parent;
    }

    isEdgePosition(position, progress) {
        if (!position) return false;

        const bb_corner = { y: 0.68065 };
        const normalizedY = (position.y + bb_corner.y) / (bb_corner.y * 2.0);
        const transitionValue = 1.0 - normalizedY;

        const padding = 1.0;
        const progressMapped = progress * 0.91 * (1.0 + padding * 2.0) - padding;

        const dissolveValue = transitionValue - progressMapped;
        return Math.abs(dissolveValue) < this.edgeThreshold;
    }

    update(progress) {
        if (!this.particleSystem || !this.mesh) return;

        const delta = 1 / 60;
        const timeOffset = performance.now() * 0.001;

        for (let i = 0; i < this.particleCount; i++) {
            const particle = this.particleData[i];

            if (particle.active) {
                particle.velocity.y -= this.gravity;
                particle.position.add(particle.velocity);

                particle.position.x += Math.sin(timeOffset + i * 0.1) * 0.0003;
                particle.position.z += Math.cos(timeOffset + i * 0.2) * 0.0003;

                particle.lifeTime += delta;
                if (particle.lifeTime >= particle.maxLifeTime) {
                    particle.active = false;
                    continue;
                }

                const lifeFactor = 1 - Math.abs((particle.lifeTime / particle.maxLifeTime) * 2 - 1);
                const scaleValue = particle.scale * lifeFactor;

                const matrix = new Matrix4();
                matrix.makeScale(scaleValue, scaleValue, scaleValue);
                matrix.setPosition(particle.position);
                this.particleSystem.setMatrixAt(i, matrix);
            } else if (Math.random() < 0.1) {
                if (this.mesh && this.mesh.geometry) {
                    const geometry = this.mesh.geometry;

                    if (geometry.attributes.position && geometry.attributes.position.array) {
                        const positionArray = geometry.attributes.position.array;
                        const vertexCount = positionArray.length / 3;

                        let foundEdgePosition = false;
                        for (let attempt = 0; attempt < 5 && !foundEdgePosition; attempt++) {
                            const randomIndex = Math.floor(Math.random() * vertexCount);
                            const x = positionArray[randomIndex * 3];
                            const y = positionArray[randomIndex * 3 + 1];
                            const z = positionArray[randomIndex * 3 + 2];

                            const position = new Vector3(x, y, z);

                            if (this.isEdgePosition(position, progress)) {
                                const randomOffset = new Vector3(
                                    (Math.random() - 0.5) * 0.01,
                                    (Math.random() - 0.5) * 0.01,
                                    (Math.random() - 0.5) * 0.01
                                );
                                position.add(randomOffset);

                                particle.position.copy(position);
                                particle.velocity.set(
                                    (Math.random() - 0.5) * 0.003,
                                    Math.random() * 0.01 + 0.005,
                                    (Math.random() - 0.5) * 0.003
                                );
                                particle.lifeTime = 0;
                                particle.maxLifeTime = Math.random() * 1.5 + 1;
                                particle.active = true;
                                foundEdgePosition = true;

                                const matrix = new Matrix4();
                                matrix.makeScale(particle.scale, particle.scale, particle.scale);
                                matrix.setPosition(particle.position);
                                this.particleSystem.setMatrixAt(i, matrix);
                            }
                        }
                    }
                }
            }
        }

        let activeCount = 0;
        for (let i = 0; i < this.particleCount; i++) {
            if (this.particleData[i].active) activeCount++;
        }

        this.particleSystem.count = activeCount;
        this.particleSystem.instanceMatrix.needsUpdate = true;

        const hue = 0.6 - progress * 0.2;
        this.particleSystem.material.color.setHSL(hue, 0.8, 0.6);

        const force = 0.0001 * Math.sin(timeOffset);
        this.packForce.set(force * 0.3, force, force * 0.5);
        this.packForceRotation.set(force * 0.001, force * 0.002, force * 0.001);
    }

    destroy() {
        this.reset();

        disposeObject(this.parent);

        if (this.particleSystem) {
            this.particleSystem.parent?.remove(this.particleSystem);
            this.particleSystem.geometry?.dispose();
            this.particleSystem.material?.dispose();
            this.particleSystem = null;
        }

        this.particleData = [];
        this.bloomSelection = [];
    }

    reset() {
        // Reset particle system
        if (this.particleSystem) {
            this.particleSystem.count = 0;
            this.particleSystem.instanceMatrix.needsUpdate = true;
        }

        // Reset particle data
        for (let i = 0; i < this.particleData.length; i++) {
            this.particleData[i].active = false;
            this.particleData[i].position.set(0, 0, 0);
            this.particleData[i].velocity.set(0, 0, 0);
            this.particleData[i].lifeTime = 0;
        }

        // Reset forces
        this.packForce.set(0, 0, 0);
        this.packForceRotation.set(0, 0, 0);
    }
}

export { BurnTransition };
